// src/pages/dashboards/practitioner/PractitionerClients.jsx
// Dashboard view: shows all linked clients with tax pack release status.
// Released clients can have their pack downloaded; outstanding ones are waiting.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../../api/practitioners";
import { Card, Select, Loading, EmptyState, Badge, Button } from "../../../components/ui";
import { Download } from "../../../components/icons";
import { money, date, relative } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

// SA tax year helper
const currentTaxYear = () => {
  const d = new Date();
  return d.getMonth() >= 2 ? d.getFullYear() + 1 : d.getFullYear();
};
const TY = currentTaxYear();
const TAX_YEARS = [TY, TY - 1, TY - 2];

// initials avatar
const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

export default function PractitionerClients() {
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [taxYear, setTaxYear] = useState(TY);

  useEffect(() => {
    setData(null);
    api.getDashboard({ tax_year: taxYear })
      .then(setData)
      .catch((e) => { toast.error(e.message); setData({ clients: [], released: 0, outstanding: 0 }); });
  }, [taxYear]);

  const downloadPack = async (e, client) => {
    e.stopPropagation();
    try {
      const blob = await api.downloadClientTaxPack(client.user_id, taxYear);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TaxPack-${client.name.replace(/\s+/g, "_")}-TY${taxYear}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Tax pack downloaded");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const clients = data?.clients || [];
  const totalDeductions = clients.reduce((s, c) => s + (c.deductions_total || 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-display font-bold">Clients</h1>
        <Select value={taxYear} onChange={(e) => setTaxYear(parseInt(e.target.value, 10))} className="w-auto">
          {TAX_YEARS.map((y) => <option key={y} value={y}>TY {y}</option>)}
        </Select>
      </div>

      {/* summary strip */}
      {data && clients.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Clients</p>
            <p className="text-xl font-bold mt-1 tabular-nums">{clients.length}</p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Released</p>
            <p className="text-xl font-bold mt-1 tabular-nums text-tb-brand">{data.released}</p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Outstanding</p>
            <p className="text-xl font-bold mt-1 tabular-nums text-amber-500">{data.outstanding}</p>
          </Card>
          <Card className="px-4 py-3 text-center">
            <p className="text-[11px] uppercase tracking-wider text-tb-muted">Combined deductions</p>
            <p className="text-xl font-bold mt-1 tabular-nums">{money(totalDeductions)}</p>
          </Card>
        </div>
      )}

      {/* client list */}
      {!data ? (
        <Loading label="Loading clients" />
      ) : clients.length === 0 ? (
        <Card className="p-2">
          <EmptyState
            title="No clients linked yet"
            hint="Share a connect code with your clients so they can grant you read-only access to their deductions."
          />
        </Card>
      ) : (
        <Card className="divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
          {clients.map((c) => (
            <button
              key={c.user_id}
              onClick={() => navigate(`/practitioner/client/${c.user_id}`)}
              className="w-full px-4 sm:px-5 py-4 flex items-center gap-4 text-left hover:bg-tb-bg-2/50 dark:hover:bg-tb-d-inset/50 transition-colors"
            >
              {/* avatar */}
              <div className="w-10 h-10 rounded-full shrink-0 grid place-items-center text-sm font-semibold text-tb-brand bg-tb-brand/10">
                {initials(c.name)}
              </div>

              {/* name + email */}
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{c.name}</p>
                <p className="text-sm text-tb-muted truncate">{c.email}</p>
              </div>

              {/* deductions */}
              <div className="hidden sm:block text-right shrink-0">
                <p className="font-semibold tabular-nums">{money(c.deductions_total)}</p>
                <p className="text-xs text-tb-muted">{c.expense_count} item{c.expense_count !== 1 ? "s" : ""}</p>
              </div>

              {/* release status + download */}
              <div className="flex items-center gap-2 shrink-0">
                {c.released ? (
                  <>
                    <Badge tone="green">Released</Badge>
                    <span
                      onClick={(e) => downloadPack(e, c)}
                      className="text-tb-brand hover:text-tb-cta cursor-pointer transition-colors"
                      title="Download tax pack"
                    >
                      <Download size={18} />
                    </span>
                  </>
                ) : (
                  <Badge tone="amber">Outstanding</Badge>
                )}
              </div>
            </button>
          ))}
        </Card>
      )}
    </div>
  );
}